import { Form } from "antd";
import { useRef } from "react";
import {
  Id,
  MenuOption,
  SelectionMode,
  DropPosition,
  Row,
} from "@silevis/reactgrid";
import {
  CellCustomType,
  CellCustomChange,
} from "@lhd/components/CloneApp/pages/SheetApp/type";
import { getDataTextByAppPart } from "@lhd/components/CloneApp/pages/SheetApp/untils";
import { AppSelectionList } from "types/app.type";
import { IPermissionItem } from "@lhd/types";
import { calcRowHeight, ROW_HEIGHT_REMEASURE_MS } from "../utils";
import { renumberRows } from "./useTableRows";
import { tableSheetContextMenuLabels } from "../contextMenuLabels";

interface UseTableHandlersProps {
  rows: Row<CellCustomType>[];
  setRows: React.Dispatch<React.SetStateAction<Row<CellCustomType>[]>>;
  columns: any;
  containerRef: React.RefObject<HTMLDivElement>;
  form: ReturnType<typeof Form.useFormInstance>;
  tablePathValue: (string | number)[];
  selectionDataList: AppSelectionList;
  isDisabled?: boolean;
  permission?: IPermissionItem;
  handleAddRow: (
    position?: "before" | "after",
    count?: number,
    insertAtRowId?: Id,
  ) => void;
  handleDeleteRow: (rowId: Id) => void;
  handleDeleteRows: (rowIds: Id[]) => void;
  onChange?: () => void;
  getManualHeight?: (clientId?: string) => number | undefined;
}

export const useTableHandlers = ({
  setRows,
  columns,
  containerRef,
  form,
  tablePathValue,
  selectionDataList,
  isDisabled,
  permission,
  handleAddRow,
  handleDeleteRows,
  onChange,
  getManualHeight,
}: UseTableHandlersProps) => {
  const rowHeightRemeasureTimers = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());

  const handleCellsChanged = (changes: CellCustomChange[]) => {
    setRows((prev) => {
      const next = [...prev];
      const valueTmp = form.getFieldValue(tablePathValue) || {};
      changes.forEach((change) => {
        if (change.rowId === "header") return;
        const { newCell, previousCell } = change;
        const { appPart } = previousCell;
        if (!appPart) return;
        const data = getDataTextByAppPart(appPart, newCell, selectionDataList);
        const rowArrayIndex = next.findIndex((r) => r.rowId === change.rowId);
        if (rowArrayIndex === -1) return;
        next[rowArrayIndex] = {
          ...next[rowArrayIndex],
          cells: next[rowArrayIndex].cells.map((c) =>
            (c as any).columnId === previousCell.columnId
              ? { ...c, ...data, status: "changed" as const }
              : c,
          ),
        };
        {
          const contentHeight = calcRowHeight(
            next[rowArrayIndex].cells,
            containerRef.current,
          );
          const manualHeight = getManualHeight?.(
            String(next[rowArrayIndex].rowId),
          );

          next[rowArrayIndex] = {
            ...next[rowArrayIndex],
            height: Math.max(contentHeight, manualHeight ?? 0),
          };
        }
        const value =
          (data as any).valueSubmitAttachment ??
          (data as any).valueSubmit ??
          (data as any).text;
        const clientIdStr = String(previousCell.clientId);
        valueTmp[clientIdStr] = {
          ...valueTmp[clientIdStr],
          client_id: previousCell.clientId,
          row_order: rowArrayIndex - 1,
          value: {
            ...valueTmp[clientIdStr]?.value,
            [String(appPart.id)]: {
              value: value,
              text_color: previousCell.text_color,
              background_color: previousCell.background_color,
            },
          },
        };
      });

      next.forEach((row, index) => {
        const rowId = row.rowId; //=== clientIdStr
        if (valueTmp[rowId]) {
          valueTmp[rowId].row_order = index - 1;
        }
      });
      form.setFieldValue(tablePathValue, valueTmp);
      return next;
    });

    const rowIds = new Set<string>();
    changes.forEach((change) => {
      if (change.rowId !== "header") rowIds.add(String(change.rowId));
    });
    rowIds.forEach((rowId) => {
      const prevTimer = rowHeightRemeasureTimers.current.get(rowId);
      if (prevTimer) clearTimeout(prevTimer);
      rowHeightRemeasureTimers.current.set(
        rowId,
        setTimeout(() => {
          rowHeightRemeasureTimers.current.delete(rowId);
          setRows((prev) => {
            const idx = prev.findIndex((r) => String(r.rowId) === rowId);
            if (idx === -1) return prev;
            const h = Math.max(
              calcRowHeight(prev[idx].cells, containerRef.current),
              getManualHeight?.(rowId) ?? 0,
            );
            if (prev[idx].height === h) return prev;
            return prev.map((r, i) => (i === idx ? { ...r, height: h } : r));
          });
        }, ROW_HEIGHT_REMEASURE_MS),
      );
    });
  };

  const handleContextMenu = (
    selectedRowIds: Id[],
    _selectedColIds: Id[],
    _selectionMode: SelectionMode,
    menuOptions: MenuOption[],
  ): MenuOption[] => {
    const dataRowIds = selectedRowIds.filter((id) => id !== "header");
    if (isDisabled || !dataRowIds.length) return menuOptions;

    const extra: MenuOption[] = [];
    const numberOfRows = selectedRowIds.length;
    if (permission?.edit !== false) {
      extra.push({
        id: "insert-row-above",
        label: tableSheetContextMenuLabels.insertRowsAbove(numberOfRows),
        handler: () => handleAddRow("before", numberOfRows, dataRowIds[0]),
      });
      extra.push({
        id: "insert-row-below",
        label: tableSheetContextMenuLabels.insertRowsBelow(numberOfRows),
        handler: () =>
          handleAddRow(
            "after",
            numberOfRows,
            dataRowIds[dataRowIds.length - 1],
          ),
      });
    }
    if (permission?.delete !== false) {
      extra.push({
        id: "delete-row",
        label: tableSheetContextMenuLabels.deleteRows,
        handler: () => handleDeleteRows(dataRowIds),
      });
    }

    return [...extra, ...menuOptions];
  };

  const handleRowsReordered = (
    targetRowId: Id,
    rowIds: Id[],
    dropPosition: DropPosition,
  ) => {
    // Hard guard: never reorder to or with header row.
    if (targetRowId === "header" || rowIds.includes("header") || !dropPosition)
      return;

    setRows((prev) => {
      const rowsToMove = prev.filter((r) => rowIds.includes(r.rowId as Id));
      const remaining = prev.filter((r) => !rowIds.includes(r.rowId as Id));
      const targetIndex = remaining.findIndex((r) => r.rowId === targetRowId);
      const insertAt =
        dropPosition === "before" ? targetIndex : targetIndex + 1;
      remaining.splice(insertAt, 0, ...rowsToMove);
      remaining?.sort((a, b) => {
        if (a.rowId === "header") return -1;
        if (b.rowId === "header") return 1;
        return 0;
      });
      const reordered = renumberRows(remaining);
      const tableValue = { ...(form.getFieldValue(tablePathValue) || {}) };
      reordered
        .filter((r) => r.rowId !== "header")
        .forEach((r, index) => {
          const id = String(r.rowId);
          tableValue[id] = {
            ...tableValue[id],
            row_order: index,
            client_id: id,
          };
        });
      form.setFieldValue(tablePathValue, tableValue);
      return reordered;
    });
    onChange?.();
  };

  return { handleCellsChanged, handleContextMenu, handleRowsReordered };
};
